import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import schoolLogo from '../school-brand.jpg'; 
import * as Styles from './styles';

function LandingDesktop() {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    // Initialize the Leaflet map centered on the UK with maxZoom restricted for privacy
    if (!mapInstanceRef.current && mapRef.current) {
      const map = L.map(mapRef.current, {
        scrollWheelZoom: false,
        attributionControl: false,
        maxZoom: 13, // 🔒 Prevents zooming in too close to residential streets
        minZoom: 6
      }).setView([54.5, -3.2], 6); // Centered on UK

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(map);

      mapInstanceRef.current = map;

      // Fetch registered instructors from your backend database API
      fetch('/api/instructor-locations')
        .then(res => res.json())
        .then(data => {
          // 🛡️ Guard: Check if the map instance still exists before touching it
          if (!mapInstanceRef.current) return;
          
          const mapInstance = mapInstanceRef.current;

          if (Array.isArray(data)) {
            data.forEach(inst => {
              if (inst.latitude && inst.longitude) {
                // Parse transmission type safely
                let transArray = ["Car (Manual)"];
                try {
                  transArray = typeof inst.transmission_type === 'string' 
                    ? JSON.parse(inst.transmission_type) 
                    : (inst.transmission_type || ["Car (Manual)"]);
                } catch (e) {
                  transArray = [inst.transmission_type || "Car (Manual)"];
                }

                // Format clean transmission badges
                const formattedBadges = transArray.map(t => {
                  const lower = String(t).toLowerCase();
                  if (lower.includes("automatic")) return "🚙 Automatic";
                  if (lower.includes("motorbike") || lower.includes("bike")) return "🏍️ Motorbike";
                  return "🚗 Manual";
                }).join(' & ');

                // 🔒 Bulletproof UK outward postcode extraction (handles spaced, unspaced, and full postcodes cleanly)
                const rawPostcode = (inst.postcode || '').replace(/\s+/g, '').toUpperCase();
                const outwardPostcode = rawPostcode.length > 3 ? rawPostcode.slice(0, rawPostcode.length - 3) : rawPostcode;

                const areaDisplay = inst.town ? `${inst.town}${outwardPostcode ? ` (${outwardPostcode})` : ''}` : (outwardPostcode ? `Area (${outwardPostcode})` : 'UK Coverage');

                // 🔒 Fuzz coordinates slightly (~200-400m offset) so exact home addresses are never exposed
                const latOffset = ((inst.id * 13) % 10 - 5) * 0.0025; 
                const lngOffset = ((inst.id * 17) % 10 - 5) * 0.0025;
                const generalizedLat = parseFloat(inst.latitude) + latOffset;
                const generalizedLng = parseFloat(inst.longitude) + lngOffset;

                // Custom circular logo marker icon
                const customPinIcon = L.divIcon({
                    className: 'custom-avatar-pin',
                    html: `
                        <div style="width: 40px; height: 40px; border-radius: 50%; overflow: hidden; border: 3px solid #0066cc; box-shadow: 0 4px 10px rgba(0,0,0,0.3); background: white;">
                            <img src="${schoolLogo}" alt="Instructor" style="width: 100%; height: 100%; object-fit: cover;" />
                        </div>
                    `,
                    iconSize: [40, 40],
                    iconAnchor: [20, 20]
                });

                const marker = L.marker([generalizedLat, generalizedLng], { icon: customPinIcon }).addTo(mapInstance);
                
                // Privacy-focused popup: Displays car type and general area instead of personal name/precise address
                marker.bindPopup(`
                  <div style="color: #000; text-align: center; font-family: sans-serif; padding: 6px;">
                    <strong style="font-size: 14px; color: #111;">${formattedBadges} Lessons</strong><br />
                    <span style="display: inline-block; margin: 4px 0; font-size: 12px; color: #2563eb; font-weight: bold;">📍 Area: ${areaDisplay}</span><br />
                    <span style="font-size: 11px; color: #555;">Verified Local Instructor Coverage</span><br />
                    <a href="/contact" style="display: inline-block; margin-top: 10px; background: #0066cc; color: #fff; padding: 6px 14px; border-radius: 6px; text-decoration: none; font-size: 11px; font-weight: bold;">
                      Enquire / Book Area ➔
                    </a>
                  </div>
                `);
              }
            });
          }
        })
        .catch(err => console.error("Could not load instructor map locations from database", err));
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div style={{ ...Styles.containerStyle, padding: '0 0 40px 0', overflowX: 'hidden' }}>
      
      {/* --- HERO SECTION --- */}
      <header style={heroSectionCustom}>
        <img 
            src={schoolLogo} 
            alt="Hero Logo" 
            style={{ width: '110px', height: '110px', borderRadius: '50%', marginBottom: '20px', border: '4px solid #0066cc', objectFit: 'cover' }} 
        />
        <h1 style={{ fontSize: 'clamp(2.2rem, 6vw, 4rem)', fontWeight: '800', marginBottom: '20px', color: '#000000', letterSpacing: '-0.02em', padding: '0 15px' }}>
          Less Admin. More Driving.
        </h1>
        <p style={{ fontSize: '1.2rem', color: '#1a202c', fontWeight: '600', maxWidth: '650px', margin: '0 auto 15px', padding: '0 15px' }}>
          Manage your students, bookings and lessons from one simple platform. Keep track of progress, share reports and stay connected with your students.
        </p>
        <p style={{ fontSize: '1.1rem', color: '#4a5568', fontStyle: 'italic', maxWidth: '600px', margin: '0 auto 30px', padding: '0 15px' }}>
          You teach. We handle the admin.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
          <Link to="/apply">
            <button style={ctaButtonCustom}>Start Managing Smarter</button>
          </Link>
        </div>
      </header>

      {/* --- LIVE DATABASE INSTRUCTOR MAP SECTION --- */}
      <div id="instructor-map" style={{ maxWidth: '1000px', margin: '10px auto 30px auto', padding: '0 20px', boxSizing: 'border-box', width: '100%' }}>        
        <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '30px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <div style={{ textAlign: 'center', marginBottom: '25px' }}>
            <h2 style={{ fontSize: '26px', color: '#1a202c', marginBottom: '10px' }}>Find Instructor Coverage Near You</h2>
            <p style={{ color: '#4a5568', fontSize: '14px' }}>
              Explore our nationwide network of professional driving instructors by area and transmission type.
            </p>
          </div>

          {/* Pure Leaflet Map Container (Pulls live generalized pins from database) */}
          <div ref={mapRef} style={{ height: '450px', width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid #cbd5e0', zIndex: 1 }} />
          
          <div style={{ textAlign: 'center', marginTop: '15px' }}>
            <Link to="/apply" style={{ color: '#0066cc', fontWeight: 'bold', textDecoration: 'none', fontSize: '14px' }}>
              Are you a driving instructor? Join our network here &rarr;
            </Link>
          </div>
        </div>
      </div>

      {/* --- SOCIAL MEDIA SECTION --- */}
      <div style={{ maxWidth: '800px', margin: '40px auto 20px auto', padding: '0 20px', textAlign: 'center' }}>
        <div style={{ background: '#ffffff', borderRadius: '12px', padding: '25px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
          <h3 style={{ fontSize: '18px', color: '#1a202c', marginBottom: '15px', fontWeight: 'bold' }}>
            Connect With LessonLane
          </h3>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
            {/* YouTube */}
            <a href="https://www.youtube.com/@lessonlaneuk" target="_blank" rel="noreferrer" style={{ ...socialBtnStyle, background: '#ff0000' }} title="YouTube">
              <svg height="22" width="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
            {/* Instagram */}
            <a href="https://www.instagram.com/lessonlaneuk/" target="_blank" rel="noreferrer" style={{ ...socialBtnStyle, background: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)' }} title="Instagram">📸</a>
            {/* TikTok */}
            <a href="https://www.tiktok.com/@lessonlaneuk" target="_blank" rel="noreferrer" style={{ ...socialBtnStyle, background: '#000000' }} title="TikTok">🎵</a>
            {/* Facebook */}
            <a href="https://facebook.com" target="_blank" rel="noreferrer" style={{ ...socialBtnStyle, background: '#3b5998' }} title="Facebook">f</a>
          </div>
        </div>
      </div>

      {/* --- FOOTER --- */}
      <div style={{ textAlign: 'center', padding: '30px 20px', background: '#1a202c', color: '#fff', marginTop: '60px' }}>
        <p style={{ color: '#718096', fontSize: '12px', margin: '0 0 5px' }}>
          &copy; 2026 LessonLane.co.uk. All rights reserved. Empowering UK Driving Education.
        </p>
      </div>

    </div>
  );
}

const heroSectionCustom = { 
  textAlign: 'center', 
  padding: '45px 15px 30px 15px',
  backgroundColor: '#ffffff', 
  color: '#111111',
  boxSizing: 'border-box',
  width: '100%'
};

const ctaButtonCustom = { 
  padding: '15px 35px', 
  fontSize: '1.1rem', 
  backgroundColor: '#0066cc', 
  color: '#ffffff', 
  border: 'none', 
  borderRadius: '30px', 
  fontWeight: 'bold', 
  cursor: 'pointer', 
  boxShadow: '0 4px 6px rgba(0,0,0,0.1)' 
};

const socialBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '45px',
  height: '45px',
  borderRadius: '10px',
  color: '#ffffff',
  fontSize: '20px',
  fontWeight: 'bold',
  textDecoration: 'none',
  boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
  transition: 'transform 0.2s ease'
};

export default LandingDesktop;